import React from "react";
import { Link } from "../../misc";
import { AtolLogo, SocialIcon } from "../../brand";
import { FlowBox } from "../../layout";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import "./Footer.scss";

interface FooterProps {
  /**
   * Pass in a form to render.
   * @deprecated @travellocal/ui can't depend on travellocal-frontend, so we pass `HubspotSignupForm` in here. When it's moved we can remove this prop.
   */
  renderForm: () => JSX.Element;
}

/**
 * Site footer of the marketing website
 *
 * @status development
 */
export const Footer: React.FC<FooterProps> = (props: FooterProps) => (
  <footer className="footer">
    <div className="width-wrapper">
      <FlowBox className="footer-links">
        <div className="link-group">
          <div className="links">
            <h3 className="text--caps">TravelLocal</h3>
            <Link to={WebRoutes.index()}>Home</Link>
            <Link to={WebRoutes.destinations()}>Destinations</Link>
            <Link to={WebRoutes.inspiration()}>Inspiration</Link>
            <Link to={WebRoutes.aboutHome()}>About us</Link>
            <Link to={WebRoutes.contactUs()}>Contact us</Link>
            <Link to={WebRoutes.enquiryForm({ campaign: "footer" })}>Send an enquiry</Link>
          </div>

          <div className="links">
            <a href="http://www.reviews.co.uk/company-reviews/store/travellocalcom"
              target="_blank"
              rel="noreferrer"
            >
              <img
                className="p-right-small"
                height="46"
                width="180"
                alt="Check out our reviews from reviews.co.uk" src="/images/sticky-footer/reviews2.png"
              />
            </a>
          </div>
        </div>

        <div className="link-group links">
          <h3 className="text--caps">More info</h3>
          <a href="https://www.gov.uk/foreign-travel-advice"
            target="_blank"
            rel="noreferrer"
          >
            UK Foreign Office Travel Advice
          </a>
          <Link to={WebRoutes.accessibility()}>Accessibility</Link>
          <Link to={WebRoutes.privacy()}>Privacy and cookies</Link>
          <Link to={WebRoutes.termsOfUse()}>Terms of use</Link>
          <Link to={WebRoutes.termsAndConditions()}>Terms and conditions</Link>
          <a href="https://careers.travellocal.com/"
            target="_blank"
            rel="noreferrer"
          >
            Careers
          </a>
          <Link to={WebRoutes.becomeAPartner()}>Become a partner</Link>
        </div>

        <div className="link-group">
          <div className="links">
            <h3 className="text--caps">Sign up to our newsletter</h3>
            <p>Find out more about where you want to go, get practical tips and{" "}
            local insights straight to your inbox. You can unsubscribe at any time, and we never send spam.</p>
            {props.renderForm()}
          </div>

          <div className="links">
            <h3 className="text--caps">Connect with us</h3>
            <div className="social">
              <a className="m-right-small"
                href="https://www.facebook.com/travellocal"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon icon="Facebook" color="white" />
              </a>
              <a className="m-right-small"
                href="https://twitter.com/travellocal"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon icon="Twitter" color="white" />
              </a>
              <a className="m-right-small"
                href="https://www.instagram.com/travellocalglobal/"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon icon="Instagram" color="white" />
              </a>
            </div>
          </div>
        </div>
      </FlowBox>

      <div className="appendix">
        <div className="atol">
          <AtolLogo type="light" width={100} />
          <p>TravelLocal is registered with ATOL, no. 11445</p>
          <p>Some of the flights and flight-inclusive holidays on this website{" "}
            are financially protected by the ATOL scheme. But ATOL protection does{" "}
            not apply to all holiday and travel services listed on this website.{" "}
            Please ask us to confirm what protection may apply to your booking.{" "}
            If you do not receive an ATOL Certificate then the booking will not be ATOL protected.{" "}
            If you do receive an ATOL Certificate but all the parts of your trip are not listed on it,{" "}
            those parts will not be ATOL protected.{" "}
            Please see our booking conditions for information,{" "}
            or for more information about financial protection and the ATOL Certificate go to:{" "}
            <a href="http://www.atol.org.uk/ATOLCertificate"
              target="_blank" rel="noreferrer">www.atol.org.uk/ATOLCertificate</a></p>
        </div>
        <div className="legal">
          Copyright © 2015-2019 TravelLocal Limited, a company registered in{" "}
          England and Wales under company number 07186581.<br />
          Our registered office is 2nd Floor, Nucleus House, 2 Lower Mortlake Rd,{" "}
          Richmond, TW9 2JA. Telephone <a href="tel:+441173257898">+44 (0)117 325 7898</a>,{" "}
          email <a href="mailto:team@travellocal.com?subject=An enquiry about TravelLocal">team@travellocal.com</a>.
        </div>
      </div>
    </div>
  </footer>
);
